import { Injectable, inject } from '@angular/core';
import { BucketFile, MediaFile } from '../types/core';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  Storage,
} from '../core/firebase/storage';
import { v4 as uuid } from 'uuid';
import { FirebaseError } from '../core/firebase/app';
import { DeviceDetectorService } from 'ngx-device-detector';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private storage = inject(Storage);
  private deviceDetectorService = inject(DeviceDetectorService);

  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  }

  dataURLtoBlob(dataurl: string): Blob {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  /** Firebase Storage delete — chat module only. Do not use for other features. */
  async remove(key: string) {
    const storageRef = ref(this.storage, key);

    try {
      await deleteObject(storageRef);
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code === 'storage/object-not-found'
      ) {
        return;
      }
      throw error;
    }
  }

  /** Firebase Storage upload — chat module only. Do not use for other features. */
  async uploadAsync(
    path: string,
    file: Blob,
    filename?: string,
    isPublic?: boolean,
    description?: string,
    mimetype: string = ''
  ): Promise<BucketFile> {
    const name = filename ? filename : `${uuid()}`;
    const key = `${path}/${name}`;
    const storageRef = ref(this.storage, key);

    // upload file
    await uploadBytes(storageRef, file);
    const publicUrl = isPublic ? await getDownloadURL(storageRef) : undefined;

    return {
      path: key,
      url: publicUrl || null,
      mimetype: mimetype,
      filename: name,
      description: description || '',
    };
  }

  previewFile(
    sourceUrl: string,
    mimeType?: string,
    title = 'Dokumentenvorschau'
  ) {
    if (
      this.deviceDetectorService.isDesktop() &&
      mimeType === 'application/pdf'
    ) {
      const previewWindow = window.open('', '_blank');
      if (previewWindow) {
        previewWindow.document.body.innerHTML = '';
        const iframe = previewWindow.document.createElement('iframe');
        iframe.src = sourceUrl;
        iframe.title = title;
        iframe.style.border = '0';
        iframe.style.position = 'absolute';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.bottom = '0';
        iframe.style.right = '0';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.setAttribute('allowfullscreen', '');
        previewWindow.document.body.appendChild(iframe);
        return;
      }
    }
    window.open(sourceUrl, '_blank');
  }

  previewMedia(file: MediaFile): void {
    if (!file) return;
    // For freshly selected files we have a Blob in memory. data: URLs are
    // blocked by Chrome when opened via window.open, so use an object URL
    // which the browser renders natively (PDF viewer, image, etc.).
    if (file.file) {
      const url = URL.createObjectURL(file.file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    if (file.displayUrl) {
      window.open(file.displayUrl, '_blank');
    }
  }
}
